import { useEffect } from "react";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";

/**
 * Query key prefixes that go stale the moment a call ends or its debrief is saved: the
 * Support Hub's item list (two names, because the old and new Support Hub pages name their
 * query differently), the details drawer's call details, and the session's own data - debrief,
 * notes, moments, recordings and timeline - shown in the drawer and on the review page.
 * Invalidating a key that is not mounted is a no-op, so listing them all is harmless.
 */
const SUPPORT_SESSION_QUERY_KEYS = [
  "videocallhistory",
  "AdmSupportApi_queryItems",
  "AdmSupportApi_getVideoCallDetails",
  "AdmSupportVideoCallSessionApi",
  "AdmSupportVideoCallSessionDataApi",
  "AdmSupportVideoCallRecordingApi",
];

/** Refetches everything a Support Hub page shows about calls, in this document. */
export function invalidateSupportSessionQueries(queryClient: QueryClient) {
  SUPPORT_SESSION_QUERY_KEYS.forEach((key) =>
    queryClient.invalidateQueries({ queryKey: [key] }),
  );
}

/**
 * Refreshes the Support Hub's item list and open details when a video call ends.
 *
 * The call is closed from the worker's popup window, which is a separate document - the
 * Support Hub has no way to know it happened, so a closed call only appeared after a manual
 * reload. The room posts 'call-ended' to its opener as its window closes (after the debrief,
 * when there is one); this listens for it.
 */
export default function useRefreshOnCallEnded() {
  const queryClient = useQueryClient();

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      // The popup is same-origin, so anything else is not ours to act on.
      if (e.origin !== window.location.origin) return;
      if (e.data !== "call-ended") return;
      invalidateSupportSessionQueries(queryClient);
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [queryClient]);
}
